'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef } from 'react';

import { Container, ContainerStyled } from '@/app/components/layouts/container';
import { BaseDealerPanel } from '@/app/components/shared/dealer/components/shared/base-dealer-panel';
import { CardLocation } from '@/app/components/shared/dealer/components/shared/card-location';
import {
  DealerEmptyState,
  EMPTY_STATE_DATA,
} from '@/app/components/shared/dealer/components/shared/empty-state';
import { InfiniteScrollList } from '@/app/components/shared/dealer/components/shared/infinite-scroll-list';
import { MobileDrawer } from '@/app/components/shared/dealer/components/shared/mobile-drawer';
import { SearchBox } from '@/app/components/shared/dealer/components/shared/smart-search/search-box';
import { DealerSearchOverlay } from '@/app/components/shared/dealer/components/shared/smart-search/search-overlay';
import { useDealerSearchKey } from '@/app/components/shared/dealer/hooks/useDealerSearchKey';
import { DEALER_SEARCH_KEY } from '@/app/components/shared/dealer/utils/dealer-constant';
import type {
  DealerLocation,
  DealerPlace,
  DealerSearch,
} from '@/app/components/shared/dealer/utils/types';
import { useDisclosure } from '@/app/hooks/useDisclosure';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';

interface SearchListDealersLocationProps {
  dealers?: DealerSearch[];
  searchValue?: string;
  setSearchValue?: (value: string) => void;
  setSelectDealer?: (item?: DealerSearch | null) => void;
  setDealerLocation?: (location: DealerLocation) => void;
  onClick?: (dealer: DealerSearch, type?: string) => void;
  onClickCurrentLocation: () => void;
  onClearSearchType?: () => void;
}

export const SearchListDealersLocation = ({
  dealers = [],
  searchValue,
  setSearchValue,
  setSelectDealer,
  setDealerLocation,
  onClick,
  onClickCurrentLocation,
  onClearSearchType,
}: SearchListDealersLocationProps) => {
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const { setSeachSuggestLocation } = useDealerSearchKey();
  const searchParams = useSearchParams();

  const { setTextSearchParams, filterSearchAutoComplete } =
    useDealerSearchKey();
  const isDesktop = useMediaQuery('(min-width: 1280px)');

  const [isShowSearch, { open: openSearch, close: closeSearch }] =
    useDisclosure(false);
  const [
    isShowSearchOverlay,
    { open: openSearchOverlay, close: closeSearchOverlay },
  ] = useDisclosure(false);

  const handleSearch = (value: string, isFromParams: boolean) => {
    safeCall(setSearchValue, value);
    safeCall(onClearSearchType);
    if (isFromParams) return;
    if (!isShowSearch) {
      openSearch();
    }
  };

  const handleOpenSearchOverlay = () => {
    if (isDesktop) return;
    openSearchOverlay();
    hiddenInputRef.current?.focus();
  };

  const handleCloseSearchOverlay = () => {
    closeSearchOverlay();
    closeSearch();
  };

  const groupDealers = useMemo(() => {
    return filterSearchAutoComplete(dealers, searchValue ?? '');
  }, [dealers, filterSearchAutoComplete, searchValue]);

  const handleSearchEnter = (value: string) => {
    handleCloseSearchOverlay();
    const currentParams = setTextSearchParams(value, dealers.length > 1);

    return setTimeout(() => {
      window.history.pushState(
        null,
        '',
        `/dealer/search?${currentParams.toString()}`
      );
    }, 0);
  };

  useEffect(() => {
    if (!searchValue && isShowSearch) {
      closeSearch();
    }
  }, [closeSearch, isShowSearch, searchValue]);

  const handleClickSuggestPlace = (item: DealerPlace) => {
    const url = setSeachSuggestLocation(item, searchParams?.toString());
    closeSearch();
    safeCall(setSearchValue, item.name);
    safeCall(setDealerLocation, { lat: item.lat, lng: item.lng });
    setTimeout(() => {
      window.history.pushState(null, '', `?${url}`);
    }, 0);
  };

  const handleClickSearchResult = (item: DealerSearch, type: string) => {
    safeCall(onClearSearchType);
    safeCall(setSelectDealer, item);

    const params = new URLSearchParams(searchParams?.toString());
    params.set(DEALER_SEARCH_KEY.Tab, type);

    if (params.has(DEALER_SEARCH_KEY.Lat)) {
      params.delete(DEALER_SEARCH_KEY.Lat);
      params.delete(DEALER_SEARCH_KEY.Lng);
    }

    setTimeout(() => {
      window.history.pushState(
        null,
        '',
        `/dealer/search/${item.uid}?${params.toString()}`
      );
    }, 0);
  };

  const onClickClearSearch = () => {
    closeSearch();
    safeCall(onClearSearchType);

    const params = new URLSearchParams(searchParams?.toString());
    if (params.has(DEALER_SEARCH_KEY.Lat)) {
      params.delete(DEALER_SEARCH_KEY.Lat);
      params.delete(DEALER_SEARCH_KEY.Lng);
    }

    setTimeout(() => {
      window.history.pushState(null, '', `/dealer/search?${params.toString()}`);
    }, 0);
  };

  const renderContent = () => {
    return (
      <Container
        as="nav"
        size="mobile-only"
        className="h-full space-y-2 pb-4 xl:pr-2"
      >
        {dealers.length > 0 ? (
          <InfiniteScrollList
            items={dealers}
            renderItem={(item: DealerSearch, index) => (
              <CardLocation
                key={item.uid}
                dealer={item}
                onClick={onClick}
                {...createDataAttributes({ test: `card-${index}` })}
              />
            )}
          />
        ) : (
          <DealerEmptyState fullScreen title={EMPTY_STATE_DATA.titleBySearch} />
        )}
      </Container>
    );
  };

  const searchBoxProps = {
    items: groupDealers,
    onChangeSearch: handleSearch,
    onEnter: handleSearchEnter,
    value: searchValue ?? '',
    onClear: onClickClearSearch,
    onClickSearchResult: handleClickSearchResult,
    onClickSuggestPlace: handleClickSuggestPlace,
  };

  return (
    <>
      <input
        ref={hiddenInputRef}
        type="text"
        aria-hidden="true"
        className="sr-only xl:hidden"
      />
      {isShowSearchOverlay ? (
        <>
          <DealerSearchOverlay
            isOpen={isShowSearchOverlay}
            onClose={handleCloseSearchOverlay}
            searchBoxProps={searchBoxProps}
          />
        </>
      ) : (
        <>
          <BaseDealerPanel
            className={cn(
              'absolute top-0 pb-1 shadow-sm xl:relative xl:pb-0 xl:shadow-none',
              {
                'overflow-hidden': !!isShowSearch,
              }
            )}
            contentClassName="hidden px-4 xl:block"
            header={
              <>
                <SearchBox
                  variant="list"
                  searchWrapperClassName={cn(
                    'h-28 pt-3 pb-1 xl:h-auto',
                    ContainerStyled({
                      size: 'mobile-only',
                      className: 'xl:px-4',
                    })
                  )}
                  {...searchBoxProps}
                  onFocus={handleOpenSearchOverlay}
                  hideAutoComplete={!isShowSearch}
                />
                {!isShowSearch && !!isDesktop && (
                  <p
                    className="text-body-2-fixed py-3 pl-4"
                    {...createDataAttributes({ test: 'title-search-result' })}
                  >
                    ผลการค้นหา {`(${dealers.length})`}
                  </p>
                )}
              </>
            }
          >
            {!isShowSearch && !!isDesktop && renderContent()}
          </BaseDealerPanel>
          <MobileDrawer
            header={
              <Container
                as="p"
                className="text-caption mb-1 text-black"
                {...createDataAttributes({ test: 'title-search-result' })}
              >
                ผลการค้นหา {`(${dealers.length})`}
              </Container>
            }
            onClickCurrentLocation={onClickCurrentLocation}
          >
            {renderContent()}
          </MobileDrawer>
        </>
      )}
    </>
  );
};
